import type {
    ProductVariantAvailabilityWithChannel as RawProductVariantAvailabilityWithChannel,
    Product as RawProduct,
    Image as RawImage,
    RawProductAttribute,
    ProductPrice as RawProductPrice,
} from "@graphql/generated/types";
import type { Product, ProductChannel, ProductMedia } from "./types";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ecco/ui/image-paths";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { activeSiteCountry } from "@config/site/site-config";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "@lib/utils/attributesUtils";
import { resolveProductDisplayName } from "./productNamePolicy";

export const normalizeProductData = (product: RawProduct, locale: string): Product => {
    const clonedProduct = removeTypename(structuredClone(product)) as RawProduct;

    const { masterData, key: sku } = clonedProduct;
    const { masterVariant } = masterData.current || {};
    const images = masterVariant?.images || [];

    if (!images?.length) {
        // If there are no images, add a placeholder image
        Logger.warn(ServiceType.NEXT_API, "ProductDetailPage missing product images", {
            tag: LogTag.MISSING_IMAGE,
            message: "No images available for product",
            sku,
            source: ServiceType.COMMERCE_TOOLS,
            country: activeSiteCountry?.toLowerCase() || "",
        });
    }

    const currentMasterData = masterData.current;
    const articleNumber = getProductAttributeValue(
        currentMasterData.masterVariant.attributesRaw,
        Attributes.articleNumber
    );

    const colorCode = getProductAttributeValue(
        currentMasterData.masterVariant.attributesRaw,
        Attributes.colorCode
    );

    const rawOccasion: unknown = getProductAttributeValue(
        currentMasterData.masterVariant.attributesRaw,
        Attributes.Occasion
    );
    const occasionValue: unknown =
        typeof rawOccasion === "string"
            ? rawOccasion
            : getProductAttributeValue(
                  currentMasterData.masterVariant.attributesRaw,
                  Attributes.Occasion,
                  locale
              );
    return {
        id: clonedProduct.id,
        colorCode,
        articleNumber,
        sku: clonedProduct.key,
        name: resolveProductDisplayName({
            name: currentMasterData.name || "",
            occasion: typeof occasionValue === "string" ? occasionValue : undefined,
        }),
        description: currentMasterData.description || "",
        attributes: currentMasterData.masterVariant.attributesRaw || [],
        productAttributes: currentMasterData.attributesRaw || [],
        media: mapImages(
            currentMasterData.masterVariant.images,
            currentMasterData.masterVariant.attributesRaw
        ),
        price: mapPrice(currentMasterData.masterVariant.price),
        variants: mapVariants(currentMasterData.variants),
    };
};

export function mapPrice(price: RawProductPrice) {
    return {
        centAmount: price?.value?.centAmount ?? 0,
        fractionDigits: price?.value?.fractionDigits ?? 0,
    };
}

export function mapAvailableChannels(
    channels: RawProductVariantAvailabilityWithChannel[]
): ProductChannel[] {
    return (
        channels?.map(({ channel, availability }) => ({
            id: channel?.id || "",
            key: channel?.key || "",
            isOnStock: availability?.isOnStock || false,
            availableQuantity: availability?.availableQuantity || 0,
        })) || []
    );
}

export function mapVariants(variants: RawProduct["masterData"]["current"]["variants"]) {
    return (
        variants?.map((variant) => ({
            id: variant.id,
            sku: variant.sku,
            price: variant.price,
            attributesRaw: variant.attributesRaw,
            availability: mapAvailableChannels(variant?.availability?.channels?.results),
        })) || []
    );
}

function mapImages(images: RawImage[], attributes: RawProductAttribute[]): ProductMedia[] {
    const productVideo = getProductAttributeValue(attributes, Attributes.ProductVideo);
    const interactiveVideo = getProductAttributeValue(attributes, Attributes.InteractiveVideo);

    const EMPTY_IMAGE: ProductMedia = {
        url: PRODUCT_IMAGE_PLACEHOLDER_URL,
        type: "image",
        label: "placeholder",
        dimensions: { width: 1200, height: 1200 },
    };

    const media: ProductMedia[] = images?.length
        ? images.map((image) => ({
              url: image.url,
              type: "image",
              label: image.label || undefined,
              dimensions: {
                  height: image.dimensions?.height || 0,
                  width: image.dimensions?.width || 0,
              },
          }))
        : [EMPTY_IMAGE];

    // Insert product video as the second item in the media array
    if (typeof productVideo === "string") {
        media.splice(1, 0, {
            type: "video",
            url: productVideo,
            dimensions: {
                height: 0,
                width: 0,
            },
        });
    }

    // Append interactive video at the end of the media array
    if (typeof interactiveVideo === "string") {
        media.push({
            type: "interactiveVideo",
            url: interactiveVideo,
            dimensions: {
                height: 0,
                width: 0,
            },
        });
    }

    return media;
}

function removeTypename(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(removeTypename);
    }

    if (obj !== null && typeof obj === "object") {
        const newObj: Record<string, any> = {};
        for (const key of Object.keys(obj)) {
            if (key !== "__typename") {
                newObj[key] = removeTypename(obj[key]);
            }
        }
        return newObj;
    }

    return obj;
}

/**
 * Prices are stored on variants (individual sizes), and since we are not preselecting a size,
 * we need to display a default price.
 * Business logic dictates using the price of the last variant (typically the largest size),
 * as it's often the most expensive (e.g., in the case of kids's shoes).
 * Once the user selects a specific variant, the selected variant's price will be used instead.
 */
export const getProductDefaultPrice = (product?: Product) => {
    if (!product?.variants) {
        return undefined;
    }

    const variantsWithCentAmount = product.variants.filter(
        (variant) => variant.price?.value?.centAmount
    );
    return variantsWithCentAmount[variantsWithCentAmount.length - 1]?.price;
};
